import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { Skeleton } from '@lifeomic/chroma-react/components/Skeleton';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import EmptyPageState from '@lifeomic/phc-web-toolkit/src/components/EmptyPageState';
import { Procedure, Search } from '@lifeomic/chromicons';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { messages } from '../messages';
import { useStyles } from '../styles';
import { MasterDetailProps } from '../types';

function AuditMasterDetail<T>(props: MasterDetailProps<T>) {
  const {
    rows,
    loading,
    loadingVariant = 'skeleton',
    emptyMessage,
    noSelectionMessage,
    rowId,
    listColumns,
    detailTitle,
    detailRows,
    renderDetail,
    selectedRowId,
    setSelectedRowId,
    searchable = false,
    getRowSearchText,
    searchLabel,
  } = props;
  const { formatMessage } = useIntl();
  const styles = useStyles();
  const tableRegionRef = useRef<HTMLDivElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const visibleRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!searchable || !query) {
      return rows;
    }
    const searchText = getRowSearchText ?? detailTitle;
    return rows.filter(row => searchText(row).toLowerCase().includes(query));
  }, [detailTitle, getRowSearchText, rows, searchQuery, searchable]);

  const selectedRow = useMemo(() => {
    if (!selectedRowId) {
      return visibleRows[0] || null;
    }
    return (
      visibleRows.find(row => rowId(row) === selectedRowId) ||
      visibleRows[0] ||
      null
    );
  }, [visibleRows, rowId, selectedRowId]);

  useEffect(() => {
    const nextSelectedRowId = selectedRow ? rowId(selectedRow) : null;
    if (nextSelectedRowId === selectedRowId) {
      return;
    }

    if (selectedRow) {
      setSelectedRowId(nextSelectedRowId);
    } else {
      setSelectedRowId(null);
    }
  }, [selectedRow, rowId, selectedRowId, setSelectedRowId]);

  useEffect(() => {
    const tableRegion = tableRegionRef.current;
    if (!tableRegion) {
      return;
    }

    const allRows = tableRegion.querySelectorAll('[data-selected="true"]');
    allRows.forEach(row => row.removeAttribute('data-selected'));

    if (selectedRowId) {
      const selectedIndex = visibleRows.findIndex(
        row => rowId(row) === selectedRowId,
      );
      if (selectedIndex !== -1) {
        const tableRows = tableRegion.querySelectorAll(
          `.${styles.tableWrapper} tbody tr`,
        );
        const selectedElement = tableRows[selectedIndex];
        if (selectedElement) {
          selectedElement.setAttribute('data-selected', 'true');
        }
      }
    }
  }, [styles.tableWrapper, rowId, visibleRows, selectedRowId]);

  if (loading) {
    return (
      <Box className={styles.auditMasterDetailRoot}>
        {loadingVariant === 'dots' ? (
          <Box fullWidth fullHeight align="center" justify="center">
            <DotLoader />
          </Box>
        ) : (
          <Skeleton width="100%" height="100%" className={styles.skeleton} />
        )}
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box className={styles.auditMasterDetailRoot}>
        <Box
          className={styles.mainContentEmpty}
          direction="row"
          align="center"
          justify="center"
        >
          <EmptyPageState
            illustration={Procedure}
            message={<Text size="subbody">{emptyMessage}</Text>}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.auditMasterDetailRoot}>
      <Box className={styles.bodyContent}>
        <Box className={styles.panelListPane}>
          {searchable ? (
            <Box className={styles.panelListFilters} direction="row" gap={1}>
              <TextField
                fullWidth
                icon={Search}
                label={searchLabel ?? formatMessage(messages.searchListLabel)}
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
              />
            </Box>
          ) : null}
          <div className={styles.tableScrollContainer} ref={tableRegionRef}>
            {visibleRows.length ? (
              <div
                className={`${styles.tableWrapper} ${styles.auditTableWrapper}`}
              >
                <TableModule
                  config={listColumns}
                  data={visibleRows}
                  isLoading={false}
                  wrapCellContent
                  onRowClick={(row: T) => setSelectedRowId(rowId(row))}
                  rowClickLabel="View details"
                />
              </div>
            ) : (
              <Box className={styles.ruleListNoMatches}>
                <Text size="subbody">{formatMessage(messages.noMatches)}</Text>
              </Box>
            )}
          </div>
        </Box>
        <Box className={styles.readingPane}>
          {!selectedRow ? (
            <Box className={styles.emptyPaper}>
              <EmptyPageState
                illustration={Procedure}
                message={<Text size="subbody">{noSelectionMessage}</Text>}
              />
            </Box>
          ) : (
            <Box direction="column" className={styles.detailPaper}>
              <Box className={styles.readingPaneHeader}>
                <Text size="subbody" weight="bold">
                  {detailTitle(selectedRow)}
                </Text>
                <Divider className={styles.divider} />
              </Box>
              {renderDetail ? (
                renderDetail(selectedRow)
              ) : (
                <Box className={styles.detailBody} direction="column" gap={1}>
                  {(detailRows?.(selectedRow) ?? []).map(item => (
                    <Box key={item.label} direction="row" gap={1}>
                      <Text size="table" weight="bold">
                        {item.label}:
                      </Text>
                      {typeof item.value === 'string' ? (
                        <Text size="table" style={{ whiteSpace: 'pre-wrap' }}>
                          {item.value}
                        </Text>
                      ) : (
                        item.value
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default AuditMasterDetail;
